import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Eye, 
  Play,
  Pause,
  Settings,
  CheckCircle,
  AlertTriangle,
  X,
  Target,
  Activity,
  Users,
  MessageSquare,
  Calendar,
  FileText,
  Database,
  Server,
  Shield,
  Bell,
  Download,
  Upload,
  RefreshCw,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  Copy,
  Save,
  TestTube,
  BarChart3,
  TrendingUp,
  PieChart,
  Timer,
  Zap,
  AlertCircle,
  CheckSquare,
  Square,
  Star,
  Award,
  TrendingDown,
  Percent,
  DollarSign,
  Building,
  UserCheck,
  UserX,
  Clock3,
  Clock4,
  Clock5,
  Clock6,
  Clock7,
  Clock8,
  Clock9,
  Clock10,
  Clock11,
  Clock12,
  Send,
  Paperclip,
  Image,
  Link,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Quote,
  Code,
  Palette,
  Type,
  Layout,
  Monitor,
  Smartphone,
  Tablet
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import AddEditEmailTemplateCategoryModal from '../../components/modals/AddEditEmailTemplateCategoryModal';
import AddEditEmailTemplateVariableModal from '../../components/modals/AddEditEmailTemplateVariableModal';

const EmailTemplateManagement = () => {
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState(null);

  useEffect(() => {
    const fetchEmailData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getEmailTemplateManagement();
        if (response.success) {
          setEmailData(response.data);
        } else {
          setError('Failed to load email template data');
        }
      } catch (err) {
        setError('Error loading email template data');
        console.error('Email template data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailData();
  }, []);

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateForm(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateForm(true);
  };

  const handleCloseTemplateForm = () => {
    setShowTemplateForm(false);
    setSelectedTemplate(null);
  };

  const handleSubmitTemplate = (templateData) => {
    //console.log('Submitting template:', templateData);
    // In a real app, this would call an API to create/update template
    setShowTemplateForm(false);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = (templateId) => {
    if (window.confirm('Are you sure you want to delete this email template?')) {
      //console.log('Deleting template:', templateId);
      // In a real app, this would call an API to delete template
    }
  };

  const handleToggleTemplateStatus = (templateId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    //console.log('Toggling template status:', templateId, newStatus);
    // In a real app, this would call an API to update template status
  };

  const handleTestTemplate = (templateId) => {
    //console.log('Testing template:', templateId);
    // In a real app, this would call an API to test the template
  };

  const handleCopyTemplate = (templateId) => {
    //console.log('Copying template:', templateId);
    // In a real app, this would call an API to copy template
  };

  const toggleTemplateExpansion = (templateId) => {
    const newExpanded = new Set(expandedTemplates);
    if (newExpanded.has(templateId)) {
      newExpanded.delete(templateId);
    } else {
      newExpanded.add(templateId);
    }
    setExpandedTemplates(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading email template management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!emailData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No email template data available</p>
        </div>
      </div>
    );
  }

  const { 
    emailTemplateManagement, 
    emailStats, 
    recentEmails, 
    emailAnalytics
  } = emailData;

  const { templates, categories, variables, campaigns } = emailTemplateManagement;

  const tabs = [
    { id: 'templates', label: 'Templates', icon: Mail },
    { id: 'categories', label: 'Categories', icon: FileText },
    { id: 'variables', label: 'Variables', icon: Code },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'inactive': return 'text-gray-600 dark:text-gray-400';
      case 'draft': return 'text-yellow-600 dark:text-yellow-400';
      case 'archived': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return X;
      case 'draft': return Clock3;
      case 'archived': return AlertTriangle;
      default: return Clock3;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'welcome': return Users;
      case 'notification': return Bell;
      case 'ticket': return MessageSquare;
      case 'marketing': return Target;
      case 'system': return Server;
      default: return Mail;
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || template.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Email Template Management
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage email templates and campaigns
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Upload className="h-4 w-4" />
                <span>Import</span>
              </button>
              <button
                onClick={handleCreateTemplate}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                 style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
              >
                <Plus className="h-4 w-4" />
                <span>Create Template</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Email Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Templates</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{emailStats.totalTemplates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Templates</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{emailStats.activeTemplates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Send className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Emails Sent Today</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{emailStats.emailsSentToday}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Rate</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{emailStats.openRate}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Email Management</h3>
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              {/* Templates Tab */}
              {activeTab === 'templates' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email Templates</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search templates..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="draft">Draft</option>
                        <option value="archived">Archived</option>
                      </select>
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Categories</option>
                        <option value="welcome">Welcome</option>
                        <option value="notification">Notification</option>
                        <option value="ticket">Ticket</option>
                        <option value="marketing">Marketing</option>
                        <option value="system">System</option>
                      </select>
                    </div>
                  </div>

                  {/* Templates List */}
                  <div className="space-y-3">
                    {filteredTemplates.map((template) => {
                      const StatusIcon = getStatusIcon(template.status);
                      const CategoryIcon = getCategoryIcon(template.category);
                      const isExpanded = expandedTemplates.has(template.id);
                      
                      return (
                        <div key={template.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center space-x-4">
                              <button
                                onClick={() => toggleTemplateExpansion(template.id)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                <CategoryIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{template.name}</h3>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{template.category}</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{template.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <StatusIcon className={`h-4 w-4 ${getStatusColor(template.status)}`} />
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                template.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                template.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                template.status === 'draft' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                              }`}>
                                {template.status}
                              </span>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleTestTemplate(template.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  title="Test Template"
                                >
                                  <TestTube className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleCopyTemplate(template.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  title="Copy Template"
                                >
                                  <Copy className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleEditTemplate(template)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  <Edit3 className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleToggleTemplateStatus(template.id, template.status)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  {template.status === 'active' ? (
                                    <Pause className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <Play className="h-4 w-4 text-gray-400" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteTemplate(template.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  <Trash2 className="h-4 w-4 text-gray-400" />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Template Details */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Template Details</h4>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Subject:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.subject}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Language:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.language}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Type:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.type}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Usage Statistics</h4>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Times Used:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.usageCount}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Open Rate:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.openRate}%</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Click Rate:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{template.clickRate}%</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Variables Used</h4>
                                <div className="flex flex-wrap gap-2">
                                  {template.variables.map((variable, index) => (
                                    <span key={index} className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                      {variable}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>Created: {new Date(template.createdDate).toLocaleDateString()}</span>
                                <span>Last Modified: {new Date(template.lastModified).toLocaleDateString()}</span>
                                <span>Last Used: {template.lastUsed ? new Date(template.lastUsed).toLocaleDateString() : 'Never'}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Categories Tab */}
              {activeTab === 'categories' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Template Categories</h2>
                    <button 
                      onClick={() => {
                        setSelectedCategory(null);
                        setShowCategoryModal(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Category</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {categories.map((category) => {
                      const getColorClasses = (color) => {
                        switch (color) {
                          case 'blue': return { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-600 dark:text-blue-400' };
                          case 'green': return { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-600 dark:text-green-400' };
                          case 'yellow': return { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-600 dark:text-yellow-400' };
                          case 'orange': return { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-600 dark:text-orange-400' };
                          case 'purple': return { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-600 dark:text-purple-400' };
                          case 'pink': return { bg: 'bg-pink-100 dark:bg-pink-900', text: 'text-pink-600 dark:text-pink-400' };
                          case 'red': return { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-600 dark:text-red-400' };
                          case 'indigo': return { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-600 dark:text-indigo-400' };
                          default: return { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-600 dark:text-blue-400' };
                        }
                      };
                      const colorClasses = getColorClasses(category.color);
                      
                      return (
                        <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
                                <FileText className={`h-5 w-5 ${colorClasses.text}`} />
                              </div>
                              <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{category.name}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setSelectedCategory(category);
                                setShowCategoryModal(true);
                              }}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Edit Category"
                            >
                              <Edit3 className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Templates:</span>
                              <span className="text-gray-900 dark:text-gray-100">{category.templateCount || 0}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Status:</span>
                              <span className={`text-sm font-medium ${
                                category.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                              }`}>
                                {category.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Variables Tab */}
              {activeTab === 'variables' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email Variables</h2>
                    <button 
                      onClick={() => {
                        setSelectedVariable(null);
                        setShowVariableModal(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Variable</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {variables.map((variable) => (
                      <div key={variable.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                              <Code className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{variable.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{variable.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{variable.usageCount || 0} uses</span>
                            <button
                              onClick={() => {
                                setSelectedVariable(variable);
                                setShowVariableModal(true);
                              }}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                              title="Edit Variable"
                            >
                              <Edit3 className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Type:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{variable.type}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Default:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{variable.defaultValue}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Required:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{variable.required ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email Analytics</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <Download className="h-4 w-4" />
                        <span>Export Report</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <PieChart className="h-8 w-8 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Template Distribution</p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">By Category</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {emailAnalytics.categoryDistribution.map((category) => (
                          <div key={category.category} className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{category.category}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{category.count} ({category.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <TrendingUp className="h-8 w-8 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Performance Trends</p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Last 30 Days</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Total Sent</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{emailAnalytics.totalSent}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Open Rate</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{emailAnalytics.openRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Click Rate</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{emailAnalytics.clickRate}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Email Activity</h3>
                    {recentEmails.map((email) => (
                      <div key={email.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{email.templateName}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {email.recipient} • {new Date(email.sentAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            email.status === 'sent' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            email.status === 'delivered' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                            email.status === 'opened' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {email.status}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{email.openTime}s</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedTemplate ? 'Edit Email Template' : 'Create New Email Template'}
              </h3>
              <button
                onClick={handleCloseTemplateForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    defaultValue={selectedTemplate?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="welcome">Welcome</option>
                    <option value="notification">Notification</option>
                    <option value="ticket">Ticket</option>
                    <option value="marketing">Marketing</option>
                    <option value="system">System</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  defaultValue={selectedTemplate?.subject || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter email subject"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  defaultValue={selectedTemplate?.description || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter template description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Content
                </label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-md">
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Bold className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Italic className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Underline className="h-4 w-4 text-gray-400" />
                      </button>
                      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-2"></div>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <AlignLeft className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <AlignCenter className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <AlignRight className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Monitor className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Tablet className="h-4 w-4 text-gray-400" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Smartphone className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  <textarea
                    rows={10}
                    defaultValue={selectedTemplate?.content || ''}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                    placeholder="Enter email content..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseTemplateForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubmitTemplate({})}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                {selectedTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Category Modal */}
      <AddEditEmailTemplateCategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setSelectedCategory(null);
        }}
        category={selectedCategory}
        onSave={(categoryData) => {
          if (selectedCategory) {
            // Update existing category
            const updatedData = {
              ...emailData,
              emailTemplateManagement: {
                ...emailData.emailTemplateManagement,
                categories: emailData.emailTemplateManagement.categories.map(cat =>
                  cat.id === selectedCategory.id
                    ? { ...cat, ...categoryData, id: cat.id }
                    : cat
                )
              }
            };
            setEmailData(updatedData);
          } else {
            // Add new category
            const newCategory = {
              id: `category-${Date.now()}`,
              ...categoryData,
              templateCount: 0
            };
            const updatedData = {
              ...emailData,
              emailTemplateManagement: {
                ...emailData.emailTemplateManagement,
                categories: [...emailData.emailTemplateManagement.categories, newCategory]
              },
              emailStats: {
                ...emailData.emailStats,
                totalCategories: emailData.emailStats.totalCategories + 1
              }
            };
            setEmailData(updatedData);
          }
          setShowCategoryModal(false);
          setSelectedCategory(null);
        }}
      />

      {/* Add/Edit Variable Modal */}
      <AddEditEmailTemplateVariableModal
        isOpen={showVariableModal}
        onClose={() => {
          setShowVariableModal(false);
          setSelectedVariable(null);
        }}
        variable={selectedVariable}
        onSave={(variableData) => {
          if (selectedVariable) {
            // Update existing variable
            const updatedData = {
              ...emailData,
              emailTemplateManagement: {
                ...emailData.emailTemplateManagement,
                variables: emailData.emailTemplateManagement.variables.map(varItem =>
                  varItem.id === selectedVariable.id
                    ? { ...varItem, ...variableData, id: varItem.id }
                    : varItem
                )
              }
            };
            setEmailData(updatedData);
          } else {
            // Add new variable
            const newVariable = {
              id: `variable-${Date.now()}`,
              ...variableData,
              usageCount: 0
            };
            const updatedData = {
              ...emailData,
              emailTemplateManagement: {
                ...emailData.emailTemplateManagement,
                variables: [...emailData.emailTemplateManagement.variables, newVariable]
              },
              emailStats: {
                ...emailData.emailStats,
                totalVariables: emailData.emailStats.totalVariables + 1
              }
            };
            setEmailData(updatedData);
          }
          setShowVariableModal(false);
          setSelectedVariable(null);
        }}
      />
    </div>
  );
};

export default EmailTemplateManagement;
